import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { jobsAPI } from '../services/api';
import { Job } from '../types';
import { Card, Button, EmptyState, SectionHeader, Pill } from '../components/ui';
import { Icon } from '../components/ui/Icon';
import { SEO } from '../components/seo/SEO';
import { jobUrl, slugify } from '../utils/slug';
import { formatJobExperience, hasExperienceRange } from '../utils/experienceRange';
import { fadeInUp, stagger } from '../utils/motion';

/**
 * Programmatic SEO landing page for "React Jobs", "Python Jobs", etc.
 * Bots get the Flask SSR version which resolves the skill via the normalized
 * skills table. Humans get this React page which fetches jobs filtered by skill name.
 */

const formatSkillFromSlug = (slug: string): string =>
  slug
    .split('-')
    .map((w) => (w.length <= 3 ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ');

const SkillLanding: React.FC = () => {
  const { skill: skillSlugParam } = useParams<{ skill: string }>();
  const skillSlug = (skillSlugParam || '').toLowerCase();
  const skillDisplay = formatSkillFromSlug(skillSlug);

  const [jobs, setJobs] = React.useState<Job[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [total, setTotal] = React.useState(0);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // Pull a wider set, then filter client-side by matching skill in job.skills
        const resp = await jobsAPI.getJobs({ per_page: 50, search: skillDisplay });
        if (cancelled) return;
        const matching = (resp.jobs || []).filter(
          (j) => (j.skills || []).some((s) => s.skill?.name && slugify(s.skill.name) === skillSlug)
        );
        setJobs(matching);
        setTotal(matching.length);
      } catch (e) {
        console.error('Failed to load skill jobs:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [skillSlug, skillDisplay]);

  const baseUrl = process.env.REACT_APP_PUBLIC_BASE_URL || 'https://talixjobs.com';
  const canonicalUrl = `${baseUrl}/jobs/skill/${skillSlug}`;

  const itemListJsonLd = {
    '@context': 'https://schema.org/',
    '@type': 'ItemList',
    name: `${skillDisplay} Jobs`,
    numberOfItems: total,
    itemListElement: jobs.slice(0, 20).map((j, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      url: `${baseUrl}${jobUrl(j)}`,
      name: j.title,
    })),
  };

  return (
    <div className="container portal-v3-page">
      <SEO
        title={`${skillDisplay} Jobs in India`}
        description={`${total || 'Open'} role${total === 1 ? '' : 's'} requiring ${skillDisplay} skills. AI-ranked roles from top companies on talixjobs.`}
        canonicalUrl={canonicalUrl}
        jsonLd={total > 0 ? itemListJsonLd : undefined}
      />

      <SectionHeader
        eyebrow="Skill"
        title={`${skillDisplay} Jobs in India`}
        description={loading ? 'Loading roles…' : `${total} open role${total === 1 ? '' : 's'} requiring ${skillDisplay}.`}
      />

      {loading ? (
        <Card padded>
          <div style={{ textAlign: 'center', padding: 'var(--s-6)', color: '#64748b' }}>
            Loading {skillDisplay} jobs…
          </div>
        </Card>
      ) : jobs.length === 0 ? (
        <EmptyState
          title={`No active ${skillDisplay} jobs right now`}
          description="Try browsing all roles or check back soon — new positions are added every day."
          icon={<Icon name="search" size={28} />}
          action={
            <Link to="/jobs" className="ui-btn ui-btn--primary ui-btn--sm">
              <span className="ui-btn__label">Browse all jobs</span>
            </Link>
          }
        />
      ) : (
        <motion.div variants={stagger(0.04)} initial="hidden" animate="visible" className="location-landing__list">
          {jobs.map((job) => (
            <motion.div key={job.id} variants={fadeInUp}>
              <Card padded className="location-landing__row">
                <div className="location-landing__row-main">
                  <Link to={jobUrl(job)} className="location-landing__title">{job.title}</Link>
                  <div className="location-landing__sub">
                    {job.company?.name && <span>{job.company.name}</span>}
                    {job.location && (<><span>•</span><span>{job.location}</span></>)}
                    {job.job_type && (<><span>•</span><span>{job.job_type}</span></>)}
                    {hasExperienceRange(job) && (<><span>•</span><span>{formatJobExperience(job)}</span></>)}
                  </div>
                  <div className="location-landing__skills">
                    {(job.skills || []).slice(0, 6).map((s) => (
                      <Pill key={s.id} tone={s.skill?.name && slugify(s.skill.name) === skillSlug ? 'info' : 'neutral'}>
                        {s.skill?.name}
                      </Pill>
                    ))}
                    {job.remote_allowed && <Pill tone="success">Remote OK</Pill>}
                  </div>
                </div>
                <div className="location-landing__row-side">
                  <Button
                    variant="primary"
                    size="sm"
                    iconRight={<Icon name="chevron-right" size={14} />}
                    onClick={() => (window.location.href = jobUrl(job))}
                  >
                    View
                  </Button>
                </div>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      )}

      <Card padded>
        <SectionHeader eyebrow="More to explore" title="Related searches" />
        <div className="location-landing__related">
          <Link to="/jobs" className="ui-btn ui-btn--secondary ui-btn--sm">
            <span className="ui-btn__label">All jobs across India</span>
          </Link>
          <Link to="/jobs/in/remote" className="ui-btn ui-btn--secondary ui-btn--sm">
            <span className="ui-btn__label">Remote {skillDisplay} roles</span>
          </Link>
          <Link to="/" className="ui-btn ui-btn--ghost ui-btn--sm">
            <span className="ui-btn__label">talixjobs home</span>
          </Link>
        </div>
      </Card>
    </div>
  );
};

export default SkillLanding;
